import { useMemo, useState } from 'react';

import {
  type DealsFieldLayoutRow,
  useDealsFieldLayout,
  useUpdateDealsFieldLayout,
} from '../../hooks/useDealsFieldLayout';
import {
  type ControlledVocabRow,
  type PromoteAsNewBody,
  type VocabViolationRow,
  useAddViolationToVocab,
  useControlledVocabList,
  useDeclineGap,
  useDismissViolation,
  useExternalOrgList,
  usePromoteGapAsNew,
  useVocabViolations,
} from '../../hooks/useVocabularies';
import { VocabulariesEditModal } from './VocabulariesEditModal';
import styles from './VocabulariesPage.module.css';

type ViewMode = 'vocab' | 'orgs' | 'violations' | 'field-order';

/**
 * Admin Vocabularies page.
 *
 * A0-lite shipped read-only browse. A1 adds the edit modal + add-row
 * affordance + violation triage panel.
 *
 * Spec: docs/archive/pre-cut-2026-08/phase-b-deals-card-fields-spec.md §9.3 + §9.5.
 */
export function VocabulariesPage() {
  const [mode, setMode] = useState<ViewMode>('vocab');

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <h1 className={styles.title}>Vocabularies</h1>
        <span className={styles.subtitle}>admin · controlled enums + canonical orgs + violation triage</span>
      </div>

      <div className={styles.modeBar}>
        <button
          className={`${styles.modeTab} ${mode === 'vocab' ? styles.modeTabActive : ''}`}
          onClick={() => setMode('vocab')}
        >
          Controlled vocabulary
        </button>
        <button
          className={`${styles.modeTab} ${mode === 'orgs' ? styles.modeTabActive : ''}`}
          onClick={() => setMode('orgs')}
        >
          External orgs
        </button>
        <button
          className={`${styles.modeTab} ${mode === 'violations' ? styles.modeTabActive : ''}`}
          onClick={() => setMode('violations')}
        >
          Violations
        </button>
        <button
          className={`${styles.modeTab} ${mode === 'field-order' ? styles.modeTabActive : ''}`}
          onClick={() => setMode('field-order')}
        >
          Deals field order
        </button>
      </div>

      {mode === 'vocab' && <VocabBrowse />}
      {mode === 'orgs' && <ExternalOrgsBrowse />}
      {mode === 'violations' && <ViolationsPanel />}
      {mode === 'field-order' && <DealsFieldOrderPanel />}
    </div>
  );
}

/**
 * Admin tool for the Deals card field order. Reads from
 * `deals_field_layout` (seeded from DEALS_FIELD_DEFS by migration
 * j8k9l0m1n2o3); on save replaces the full ordering.
 *
 * Drag-and-drop is overkill for 17 rows — up/down arrows are
 * sufficient and avoid a new dep. Width + visibility toggle inline.
 */
function DealsFieldOrderPanel() {
  const query = useDealsFieldLayout();
  const update = useUpdateDealsFieldLayout();
  const [rows, setRows] = useState<DealsFieldLayoutRow[] | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  // Drag-and-drop state. `draggingIdx` is the row currently being
  // dragged; `dropTargetIdx` is the index the drop would land at
  // (shown as a horizontal indicator line). Must live before any
  // early return so the Hook order stays stable.
  const [draggingIdx, setDraggingIdx] = useState<number | null>(null);
  const [dropTargetIdx, setDropTargetIdx] = useState<number | null>(null);

  // Seed local state from the server on first load + after each save.
  // We work on a local copy so reorder/toggle UI is instant.
  const serverRows = query.data?.fields;
  const baseline = useMemo(
    () =>
      serverRows
        ? serverRows
            .slice()
            .sort((a, b) => a.display_order - b.display_order)
            .map((r) => ({ ...r }))
        : null,
    [serverRows],
  );

  // Hydrate local state once when the server data first arrives.
  if (rows === null && baseline) {
    setRows(baseline);
  }

  if (query.isLoading) {
    return <div className={styles.loading}>Loading field layout…</div>;
  }
  if (query.error || !rows) {
    return (
      <div className={styles.empty}>
        Failed to load field layout. Check that migration{' '}
        <code>j8k9l0m1n2o3</code> has run.
      </div>
    );
  }

  const move = (idx: number, dir: -1 | 1) => {
    const next = idx + dir;
    if (next < 0 || next >= rows.length) return;
    const copy = rows.slice();
    [copy[idx], copy[next]] = [copy[next], copy[idx]];
    setRows(copy);
  };

  const handleDragStart = (idx: number) => (e: React.DragEvent<HTMLTableRowElement>) => {
    setDraggingIdx(idx);
    // Required for Firefox to start the drag.
    e.dataTransfer.effectAllowed = 'move';
    try {
      e.dataTransfer.setData('text/plain', String(idx));
    } catch {
      // Some browsers throw on certain mime types; the actual reorder
      // logic doesn't depend on the dataTransfer payload — we use
      // draggingIdx state directly.
    }
  };

  const handleDragOver = (idx: number) => (e: React.DragEvent<HTMLTableRowElement>) => {
    if (draggingIdx === null) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dropTargetIdx !== idx) setDropTargetIdx(idx);
  };

  const handleDrop = (idx: number) => (e: React.DragEvent<HTMLTableRowElement>) => {
    e.preventDefault();
    const from = draggingIdx;
    setDraggingIdx(null);
    setDropTargetIdx(null);
    if (from === null || from === idx) return;
    const copy = rows.slice();
    const [moved] = copy.splice(from, 1);
    // When dragging downward, the target index shifts left by one
    // after the splice removes the source row.
    const to = from < idx ? idx - 1 : idx;
    copy.splice(to, 0, moved);
    setRows(copy);
  };

  const handleDragEnd = () => {
    setDraggingIdx(null);
    setDropTargetIdx(null);
  };

  const updateField = (
    idx: number,
    patch: Partial<DealsFieldLayoutRow>,
  ) => {
    const copy = rows.slice();
    copy[idx] = { ...copy[idx], ...patch };
    setRows(copy);
  };

  const dirty =
    baseline &&
    JSON.stringify(rows.map((r) => [r.field_name, r.width, r.visible])) !==
      JSON.stringify(
        baseline.map((r) => [r.field_name, r.width, r.visible]),
      );

  const onSave = () => {
    setSaveError(null);
    // Renumber display_order from the local visual order (10, 20, 30,
    // …) so admins don't have to think about the integer keys.
    const renumbered = rows.map((r, i) => ({
      ...r,
      display_order: (i + 1) * 10,
    }));
    update.mutate(
      { fields: renumbered },
      {
        onError: (err) => setSaveError(err.message || 'Save failed'),
      },
    );
  };

  const onReset = () => {
    if (baseline) setRows(baseline.map((r) => ({ ...r })));
  };

  return (
    <div className={styles.fieldOrderPanel}>
      <div className={styles.fieldOrderHeader}>
        <p className={styles.fieldOrderHelp}>
          Reorder the editable fields on the Deals card. Visible=off
          hides a field from the card grid (it still ships in the
          aggregator payload — just not rendered). Width determines
          whether the field pairs with its neighbour in the
          (future) 2-col layout. Save commits to{' '}
          <code>deals_field_layout</code>.
        </p>
        <div className={styles.fieldOrderActions}>
          <button
            type="button"
            className={styles.fieldOrderBtnSecondary}
            disabled={!dirty || update.isPending}
            onClick={onReset}
          >
            Reset
          </button>
          <button
            type="button"
            className={styles.fieldOrderBtnPrimary}
            disabled={!dirty || update.isPending}
            onClick={onSave}
          >
            {update.isPending ? 'Saving…' : 'Save order'}
          </button>
        </div>
      </div>
      {saveError && (
        <div className={styles.fieldOrderError}>{saveError}</div>
      )}
      <table className={styles.fieldOrderTable}>
        <thead>
          <tr>
            <th></th>
            <th>#</th>
            <th>Field name</th>
            <th>Zone</th>
            <th>Width</th>
            <th>Visible</th>
            <th>Reorder</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => {
            const rowClasses = [
              !row.visible ? styles.fieldOrderRowHidden : '',
              draggingIdx === idx ? styles.fieldOrderRowDragging : '',
              dropTargetIdx === idx && draggingIdx !== null && draggingIdx !== idx
                ? styles.fieldOrderRowDropTarget
                : '',
            ]
              .filter(Boolean)
              .join(' ');
            return (
              <tr
                key={row.field_name}
                className={rowClasses}
                draggable
                onDragStart={handleDragStart(idx)}
                onDragOver={handleDragOver(idx)}
                onDrop={handleDrop(idx)}
                onDragEnd={handleDragEnd}
              >
                <td
                  className={styles.fieldOrderDragHandle}
                  title="Drag to reorder"
                  aria-label="Drag to reorder"
                >
                  ⋮⋮
                </td>
                <td>{idx + 1}</td>
                <td><code>{row.field_name}</code></td>
                <td>{row.zone}</td>
                <td>
                  <select
                    value={row.width}
                    onChange={(e) =>
                      updateField(idx, {
                        width: e.target.value as 'full' | 'half',
                      })
                    }
                  >
                    <option value="full">full</option>
                    <option value="half">half</option>
                  </select>
                </td>
                <td>
                  <input
                    type="checkbox"
                    checked={row.visible}
                    onChange={(e) =>
                      updateField(idx, { visible: e.target.checked })
                    }
                  />
                </td>
                <td>
                  <button
                    type="button"
                    className={styles.fieldOrderArrow}
                    disabled={idx === 0}
                    onClick={() => move(idx, -1)}
                    title="Move up"
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    className={styles.fieldOrderArrow}
                    disabled={idx === rows.length - 1}
                    onClick={() => move(idx, 1)}
                    title="Move down"
                  >
                    ↓
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function VocabBrowse() {
  // Pull ALL rows (incl. inactive) so admin can re-activate soft-deleted entries.
  const all = useControlledVocabList(null, true);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [editing, setEditing] = useState<ControlledVocabRow | null>(null);
  const [addingNew, setAddingNew] = useState(false);

  const vocabKeys = useMemo(() => {
    if (!all.data) return [];
    return Object.entries(all.data.by_vocab_key).sort((a, b) =>
      a[0].localeCompare(b[0]),
    );
  }, [all.data]);

  const filteredRows = useMemo(() => {
    if (!all.data) return [];
    if (!selectedKey) return all.data.rows;
    return all.data.rows.filter((r) => r.vocab_key === selectedKey);
  }, [all.data, selectedKey]);

  if (all.isLoading) return <div className={styles.loading}>Loading vocabulary…</div>;
  if (all.error) return <div className={styles.empty}>Failed to load vocabulary.</div>;
  if (!all.data || vocabKeys.length === 0) {
    return (
      <div className={styles.empty}>
        No vocabulary rows yet. Run the seed
        (<code>python -m app.seed.seed_controlled_vocabulary</code>) or trigger
        the boot seeder.
      </div>
    );
  }

  return (
    <>
      <div className={styles.layout}>
        <div className={styles.sidebar}>
          <div
            className={`${styles.sidebarItem} ${selectedKey === null ? styles.sidebarItemActive : ''}`}
            onClick={() => setSelectedKey(null)}
          >
            <span>All</span>
            <span className={styles.sidebarCount}>{all.data.rows.length}</span>
          </div>
          {vocabKeys.map(([key, count]) => (
            <div
              key={key}
              className={`${styles.sidebarItem} ${selectedKey === key ? styles.sidebarItemActive : ''}`}
              onClick={() => setSelectedKey(key)}
            >
              <span>{key}</span>
              <span className={styles.sidebarCount}>{count}</span>
            </div>
          ))}
        </div>

        <div className={styles.content}>
          <div className={styles.actionBar}>
            <span className={styles.subtitle}>
              {filteredRows.length} row{filteredRows.length === 1 ? '' : 's'}
              {selectedKey ? ` · ${selectedKey}` : ''}
            </span>
            <button
              className={styles.addBtn}
              onClick={() => setAddingNew(true)}
            >
              + Add value
            </button>
          </div>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>vocab_key</th>
                <th>value</th>
                <th>label</th>
                <th>description (terse)</th>
                <th>attrs</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((row) => (
                <tr key={row.id} className={row.active ? '' : styles.inactiveRow}>
                  <td>{row.vocab_key}</td>
                  <td className={styles.valueCell}>
                    {row.value}
                    {!row.active && <span className={styles.inactiveBadge}>inactive</span>}
                  </td>
                  <td>{row.label}</td>
                  <td className={styles.descCell}>{row.description_terse ?? '—'}</td>
                  <td className={styles.attrCell}>
                    {Object.entries(row.attrs).length === 0
                      ? '—'
                      : Object.entries(row.attrs)
                          .map(([k, v]) => `${k}=${v}`)
                          .join(', ')}
                  </td>
                  <td>
                    <button
                      className={styles.editBtn}
                      onClick={() => setEditing(row)}
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filteredRows.length === 0 && (
            <div className={styles.empty}>No rows in this vocab_key.</div>
          )}
        </div>
      </div>

      {editing && (
        <VocabulariesEditModal
          initial={editing}
          onClose={() => setEditing(null)}
        />
      )}
      {addingNew && (
        <VocabulariesEditModal
          initial={null}
          defaultVocabKey={selectedKey ?? undefined}
          onClose={() => setAddingNew(false)}
        />
      )}
    </>
  );
}

function ExternalOrgsBrowse() {
  const [search, setSearch] = useState('');
  const list = useExternalOrgList(search);

  return (
    <div>
      <div className={styles.notice}>
        Canonical org entities — populated by smoke-test seed + the org-enrichment
        postprocessor (Phase 3a-postprocessor, not yet shipped). Search by name
        or domain.
      </div>
      <input
        type="text"
        className={styles.searchInput}
        placeholder="Search by name or domain…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      {list.isLoading && <div className={styles.loading}>Loading…</div>}
      {list.error && <div className={styles.empty}>Failed to load external orgs.</div>}
      {list.data && list.data.rows.length === 0 && (
        <div className={styles.empty}>
          {search ? `No orgs match "${search}".` : 'No external_org rows yet.'}
        </div>
      )}
      {list.data && list.data.rows.length > 0 && (
        <>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>canonical_name</th>
                <th>industry</th>
                <th>hq_location</th>
                <th>domains</th>
                <th>assignments</th>
                <th>origin</th>
                <th>status</th>
              </tr>
            </thead>
            <tbody>
              {list.data.rows.map((row) => (
                <tr key={row.id}>
                  <td>{row.canonical_name}</td>
                  <td className={styles.descCell}>{row.industry ?? '—'}</td>
                  <td className={styles.descCell}>{row.hq_location ?? '—'}</td>
                  <td className={styles.valueCell}>{row.domain_count}</td>
                  <td className={styles.valueCell}>{row.assignment_count}</td>
                  <td className={styles.attrCell}>{row.origin}</td>
                  <td className={styles.attrCell}>{row.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {list.data.total > list.data.rows.length && (
            <div className={styles.subtitle} style={{ marginTop: 12 }}>
              Showing {list.data.rows.length} of {list.data.total}. Refine
              search to see more.
            </div>
          )}
        </>
      )}
    </div>
  );
}

/**
 * The vocab worklist: membership rejections AND vocabulary gaps.
 *
 * ⛔ No body text. There is no `phrase` field on the wire and no ref list — the page is firm-wide and
 * `vocab_violation_log` carries no `permission_class`, so a grounding sentence would be a firm-wide
 * body leak (primitive spec §5). `description` / `nearest[].why_not` are machine-written definitions
 * scrubbed server-side; an `[email]` / `[url]` marker means text was redacted.
 */
function ViolationsPanel() {
  const [statusFilter, setStatusFilter] = useState<
    'open' | 'added_to_vocab' | 'dismissed' | 'declined' | 'promoted'
  >('open');
  const violations = useVocabViolations(statusFilter);
  const addToVocab = useAddViolationToVocab();
  const dismiss = useDismissViolation();
  const promote = usePromoteGapAsNew();
  const decline = useDeclineGap();
  const [error, setError] = useState<string | null>(null);
  const [promoting, setPromoting] = useState<VocabViolationRow | null>(null);

  async function handleAdd(v: VocabViolationRow) {
    setError(null);
    try {
      await addToVocab.mutateAsync({ violationId: v.id });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Add to vocab failed');
    }
  }

  async function handleDismiss(v: VocabViolationRow) {
    setError(null);
    try {
      await dismiss.mutateAsync({ violationId: v.id });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Dismiss failed');
    }
  }

  async function handleDecline(v: VocabViolationRow) {
    setError(null);
    try {
      await decline.mutateAsync({ violationId: v.id });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Decline failed');
    }
  }

  return (
    <div>
      <div className={styles.notice}>
        Two kinds of row. A <strong>value</strong> row is a value a model reached for that is not in
        the register — promote it or dismiss it. A <strong>gap</strong> row is a model saying "none of
        these fits, and it matters", with a proposed name and an argument against the nearest values;
        promoting one creates the register value and re-checks the stored proposals for exactly the
        subjects that produced it. Declining is sticky and writes nothing.
      </div>

      <div className={styles.violationFilter}>
        {(['open', 'promoted', 'declined', 'added_to_vocab', 'dismissed'] as const).map((s) => (
          <button
            key={s}
            className={`${styles.violationFilterBtn} ${statusFilter === s ? styles.violationFilterBtnActive : ''}`}
            onClick={() => setStatusFilter(s)}
          >
            {s}
          </button>
        ))}
      </div>

      {/* `modalError`, not a `styles.error` this module never defined — that rendered
          className="undefined", so every promote/decline failure came through UNSTYLED,
          including the D-P9 409 whose whole point is naming the missing piece (F11). */}
      {error && <div className={styles.modalError}>{error}</div>}
      {violations.isLoading && <div className={styles.loading}>Loading violations…</div>}
      {violations.data && violations.data.rows.length === 0 && (
        <div className={styles.empty}>
          {statusFilter === 'open'
            ? 'No open violations or gaps. 🎉'
            : `Nothing in status: ${statusFilter}.`}
        </div>
      )}
      {violations.data && violations.data.rows.length > 0 && (
        <table className={styles.table}>
          <thead>
            <tr>
              <th>kind</th>
              <th>vocab_key</th>
              <th>value / slug</th>
              <th>proposed</th>
              <th>hits</th>
              <th>subjects</th>
              <th>nearest</th>
              <th>latest subject</th>
              <th>producer</th>
              <th>backfill</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {violations.data.rows.map((v) => (
              <tr key={v.id}>
                <td className={styles.attrCell}>{v.kind}</td>
                <td>{v.vocab_key}</td>
                <td className={styles.valueCell}>{v.raw_value}</td>
                <td className={styles.attrCell}>
                  {v.proposed_label ? (
                    <>
                      <div>{v.proposed_label}</div>
                      {v.description && <div className={styles.attrCell}>{v.description}</div>}
                    </>
                  ) : (
                    '—'
                  )}
                </td>
                <td className={styles.valueCell}>{v.count}</td>
                <td className={styles.valueCell}>
                  {v.subject_count}
                  {v.kind === 'gap' && !v.surfaced ? ' (below threshold)' : ''}
                </td>
                <td className={styles.attrCell}>
                  {v.nearest.length > 0
                    ? v.nearest.map((n) => `${n.value}: ${n.why_not ?? '—'}`).join(' · ')
                    : '—'}
                </td>
                <td className={styles.attrCell}>
                  {v.subject_ref ? `${v.subject_kind ?? ''}:${v.subject_ref}` : '—'}
                </td>
                <td className={styles.attrCell}>
                  {v.producer_kind ? `${v.producer_kind}${v.record_ref ? ` (${v.record_ref})` : ''}` : '—'}
                </td>
                <td className={styles.attrCell}>
                  {/* ⚠ "queued" and "waiting on a handler nobody has written" look identical to
                      an admin otherwise, and only one of them is something the backfill will get to
                      on its own. A parked row re-offers itself once a day and heals the moment a
                      producer registers — so it is not stuck, but it is not progressing either. */}
                  {v.backfill_subjects === null
                    ? v.status !== 'promoted'
                      ? '—'
                      : v.backfill_awaiting_executor_at
                        ? 'parked — awaiting handler'
                        : 'queued'
                    : `filled ${v.backfill_filled ?? 0} · human ${v.backfill_refused_human ?? 0} · unmapped ${v.backfill_still_unmapped ?? 0}`}
                </td>
                <td>
                  {statusFilter === 'open' ? (
                    <div className={styles.violationActions}>
                      {v.kind === 'gap' ? (
                        <>
                          <button
                            className={styles.violationAddBtn}
                            onClick={() => setPromoting(v)}
                            disabled={promote.isPending}
                          >
                            Promote as new
                          </button>
                          <button
                            className={styles.violationDismissBtn}
                            onClick={() => handleDecline(v)}
                            disabled={decline.isPending}
                          >
                            Decline
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            className={styles.violationAddBtn}
                            onClick={() => handleAdd(v)}
                            disabled={addToVocab.isPending}
                          >
                            Add to vocab
                          </button>
                          <button
                            className={styles.violationDismissBtn}
                            onClick={() => handleDismiss(v)}
                            disabled={dismiss.isPending}
                          >
                            Dismiss
                          </button>
                        </>
                      )}
                    </div>
                  ) : (
                    <span className={styles.attrCell}>—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {promoting && (
        <PromoteGapForm
          row={promoting}
          pending={promote.isPending}
          onCancel={() => setPromoting(null)}
          onSubmit={async (body) => {
            setError(null);
            try {
              await promote.mutateAsync({ violationId: promoting.id, ...body });
              setPromoting(null);
            } catch (e) {
              setError(e instanceof Error ? e.message : 'Promote failed');
            }
          }}
        />
      )}
    </div>
  );
}

/**
 * The attrs a register row needs, confirmed rather than accepted.
 *
 * Pre-filled from the gap where the gap has an opinion, and required regardless: the legacy one-click
 * route is what shipped values with no attrs at all, and an attr-less value is one the tree cannot
 * rank, the prompt cannot describe and the projection cannot place.
 */
function PromoteGapForm({
  row,
  pending,
  onCancel,
  onSubmit,
}: {
  row: VocabViolationRow;
  pending: boolean;
  onCancel: () => void;
  onSubmit: (body: PromoteAsNewBody) => void;
}) {
  const [label, setLabel] = useState(row.proposed_label ?? '');
  const [descriptionTerse, setDescriptionTerse] = useState(row.description ?? '');
  const [domain, setDomain] = useState('deal');
  const [materialityRank, setMaterialityRank] = useState('50');
  const [requiredQualifiers, setRequiredQualifiers] = useState('');

  const complete =
    label.trim() !== '' &&
    descriptionTerse.trim() !== '' &&
    domain.trim() !== '' &&
    materialityRank.trim() !== '' &&
    !Number.isNaN(Number(materialityRank));

  return (
    <div className={styles.notice}>
      <div>
        <strong>Promote</strong> <code>{row.vocab_key}</code> = <code>{row.raw_value}</code>
        {row.vocab_key === 'relationship_type' && (
          <span>
            {' '}
            — a contract-bearing key: the server refuses this unless the new type has TYPE_QUALIFIERS
            coverage (the E0-AB contract).
          </span>
        )}
      </div>
      <label>
        label
        <input value={label} onChange={(e) => setLabel(e.target.value)} />
      </label>
      <label>
        description_terse
        <input
          value={descriptionTerse}
          onChange={(e) => setDescriptionTerse(e.target.value)}
        />
      </label>
      <label>
        domain
        <input value={domain} onChange={(e) => setDomain(e.target.value)} />
      </label>
      <label>
        materiality_rank
        <input
          value={materialityRank}
          onChange={(e) => setMaterialityRank(e.target.value)}
        />
      </label>
      <label>
        required_qualifiers (comma separated, may be empty)
        <input
          value={requiredQualifiers}
          onChange={(e) => setRequiredQualifiers(e.target.value)}
        />
      </label>
      <div className={styles.violationActions}>
        <button
          className={styles.violationAddBtn}
          disabled={!complete || pending}
          onClick={() =>
            onSubmit({
              label: label.trim(),
              description_terse: descriptionTerse.trim(),
              domain: domain.trim(),
              materiality_rank: Number(materialityRank),
              required_qualifiers: requiredQualifiers
                .split(',')
                .map((s) => s.trim())
                .filter((s) => s !== ''),
            })
          }
        >
          Create value + enqueue backfill
        </button>
        <button className={styles.violationDismissBtn} onClick={onCancel} disabled={pending}>
          Cancel
        </button>
      </div>
    </div>
  );
}
